import { useState, useCallback } from 'react';
import {
  Stack,
  Typography,
  Button,
  Chip,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import * as ExcelJS from 'exceljs';

import { useAlert } from '@/contexts/Alert/AlertContext';
import { useLoading } from '@/shared/context/UIContextProvider';
import { reportAppError } from '@/shared/monitoring';
import { LeaseLifecycle, DoneSubState, LeaseSubState } from '@urbanmix-tech/shared-js';
import { saveUnits } from '@/data';
import { FileUploadButton } from '@/components/shared/FileUploads/FileUploadButton';

interface CreateUnitsFromEntrataProps {
  projectId: string;
  buildingId: number;
  onComplete?: () => void;
}

interface ParsedUnit {
  id: string;
  projectId: string;
  buildingId: number;
  name: string;
  line: string;
  type: string;
  floor: number;
  meshId: string | null;
  numBedrooms: number;
  numBathrooms: number;
  sqft: number;
  marketRent: number | null;
  residentDeposit: number | null;
  actualRent: number | null;
  currentState: string | string[];
  currentSubState: string;
  leaseStartDate: Date | null;
  leaseEndDate: Date | null;
  tenantName?: string;
}

// Extract floor and type from unit ID (e.g., "A1" → floor guess, type "A")
function extractFloorAndLine(unitId: string): { floor: number; line: string } {
  // Try patterns like "A1", "B2", "F10"
  const match = unitId.match(/^([A-Z]+)(\d+)$/i);
  if (match) {
    return { floor: 1, line: match[1] };
  }
  // Try patterns like "1A", "2B"
  const match2 = unitId.match(/^(\d+)([A-Z]+)$/i);
  if (match2) {
    return { floor: parseInt(match2[1], 10), line: match2[2] };
  }
  return { floor: 1, line: unitId };
}

// Parse Entrata Excel file - auto-detects header row by looking for "Unit" column
async function parseEntrataExcel(file: File): Promise<{ headers: string[]; records: string[][] }> {
  const workbook = new ExcelJS.Workbook();
  const arrayBuffer = await file.arrayBuffer();
  await workbook.xlsx.load(arrayBuffer);

  const worksheet = workbook.worksheets[0];

  let headerRowNum = 0;
  for (let rowNum = 1; rowNum <= Math.min(10, worksheet.rowCount); rowNum++) {
    const row = worksheet.getRow(rowNum);
    let found = false;
    row.eachCell({ includeEmpty: false }, (cell) => {
      if (String(cell.value).trim() === 'Unit') found = true;
    });
    if (found) {
      headerRowNum = rowNum;
      break;
    }
  }

  if (headerRowNum === 0) {
    throw new Error('Could not find header row with "Unit" column');
  }

  const headerRow = worksheet.getRow(headerRowNum);
  const headers: string[] = [];
  headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    headers[colNumber - 1] = cell.value ? String(cell.value).trim() : '';
  });

  const records: string[][] = [];
  for (let rowNum = headerRowNum + 1; rowNum <= worksheet.rowCount; rowNum++) {
    const row = worksheet.getRow(rowNum);
    const record: string[] = [];
    let hasData = false;

    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      let cellValue = '';
      const value = cell.value;
      if (value !== null && value !== undefined) {
        if (value instanceof Date) {
          cellValue = dayjs(value).format('MM/DD/YYYY');
        } else if (typeof value === 'object' && 'result' in value) {
          cellValue = String(value.result || '');
        } else {
          cellValue = String(value);
        }
        if (cellValue.trim()) hasData = true;
      }
      record[colNumber - 1] = cellValue;
    });

    const firstValue = record[0] || '';
    const unitValue = record[1] || '';
    if (hasData && !firstValue.toLowerCase().includes('grand total') && unitValue.trim()) {
      records.push(record);
    }
  }

  return { headers, records };
}

export function CreateUnitsFromEntrata({
  projectId,
  buildingId,
  onComplete,
}: CreateUnitsFromEntrataProps) {
  const { showAlert } = useAlert();
  const { createLoading } = useLoading();

  const [parsedUnits, setParsedUnits] = useState<ParsedUnit[]>([]);
  const [unitFileName, setUnitFileName] = useState<string | null>(null);
  const [tenantFileName, setTenantFileName] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleUnitDirectoryFile = useCallback(
    async (file: File) => {
      const loader = createLoading();
      loader.startLoading({ title: 'Reading Unit Directory...' });

      try {
        const { headers, records } = await parseEntrataExcel(file);

        const unitIdx = headers.indexOf('Unit');
        const sqftIdx = headers.indexOf('Size (Sqft)');
        const marketRentIdx = headers.indexOf('Market Rent');
        const depositIdx = headers.indexOf('Deposit');
        const bedroomsIdx = headers.indexOf('Bedrooms');
        const bathroomsIdx = headers.indexOf('Bathrooms');
        const statusIdx = headers.indexOf('Status');

        const units: ParsedUnit[] = records.map((record) => {
          const unitId = String(record[unitIdx] || '').trim();
          const { floor, line } = extractFloorAndLine(unitId);
          const status = String(record[statusIdx] || '');
          const isOccupied = status.toLowerCase().startsWith('occupied');

          return {
            id: unitId,
            projectId,
            buildingId,
            name: unitId,
            line,
            type: line,
            floor,
            meshId: null,
            numBedrooms: bedroomsIdx >= 0 ? parseFloat(record[bedroomsIdx]) || 0 : 0,
            numBathrooms: bathroomsIdx >= 0 ? parseFloat(record[bathroomsIdx]) || 0 : 0,
            sqft: sqftIdx >= 0 ? parseFloat(record[sqftIdx]) || 0 : 0,
            marketRent: marketRentIdx >= 0 ? parseFloat(String(record[marketRentIdx]).replace(/,/g, '')) || null : null,
            residentDeposit: depositIdx >= 0 ? parseFloat(String(record[depositIdx]).replace(/,/g, '')) || null : null,
            actualRent: null,
            currentState: isOccupied ? LeaseLifecycle.DONE : LeaseLifecycle.LEASE,
            currentSubState: isOccupied ? DoneSubState.Occupied : LeaseSubState.Publish,
            leaseStartDate: null,
            leaseEndDate: null,
          };
        });

        setParsedUnits(units);
        setUnitFileName(file.name);
        loader.stopLoading();
      } catch (err) {
        loader.stopLoading();
        reportAppError(err as Error);
        showAlert('Failed to parse Unit Directory file', 'error');
      }
    },
    [createLoading, showAlert, projectId, buildingId]
  );

  const handleTenantListingFile = useCallback(
    async (file: File) => {
      const loader = createLoading();
      loader.startLoading({ title: 'Reading Tenant Listing...' });

      try {
        const { headers, records } = await parseEntrataExcel(file);

        const unitIdx = headers.indexOf('Unit');
        const tenantIdx = headers.indexOf('Tenant');
        const leaseFromIdx = headers.indexOf('Lease From');
        const leaseToIdx = headers.indexOf('Lease To');

        // Build a map of unit → tenant data (first tenant per unit wins, skip roommates without end dates)
        const tenantMap = new Map<string, { tenantName: string; leaseFrom: string; leaseTo: string }>();
        for (const record of records) {
          const unitId = String(record[unitIdx] || '').trim();
          const leaseTo = record[leaseToIdx] || '';
          // Skip entries without a lease end date (roommates etc.)
          if (!unitId || !leaseTo) continue;
          // Only keep the first entry per unit (main tenant)
          if (tenantMap.has(unitId)) continue;
          tenantMap.set(unitId, {
            tenantName: String(record[tenantIdx] || '').trim(),
            leaseFrom: record[leaseFromIdx] || '',
            leaseTo,
          });
        }

        // Merge tenant data into parsed units
        setParsedUnits((prev) =>
          prev.map((unit) => {
            const tenant = tenantMap.get(unit.id);
            if (tenant) {
              return {
                ...unit,
                tenantName: tenant.tenantName,
                leaseStartDate: tenant.leaseFrom ? dayjs(tenant.leaseFrom, 'MM/DD/YYYY').toDate() : null,
                leaseEndDate: tenant.leaseTo ? dayjs(tenant.leaseTo, 'MM/DD/YYYY').toDate() : null,
              };
            }
            return unit;
          })
        );

        setTenantFileName(file.name);
        loader.stopLoading();
      } catch (err) {
        loader.stopLoading();
        reportAppError(err as Error);
        showAlert('Failed to parse Tenant Listing file', 'error');
      }
    },
    [createLoading, showAlert]
  );

  const handleCreateUnits = async () => {
    if (parsedUnits.length === 0) return;

    setSaving(true);
    const loader = createLoading();
    loader.startLoading({ title: 'Creating units...' });

    try {
      await saveUnits(projectId, parsedUnits as any);
      showAlert(`Successfully created ${parsedUnits.length} units!`, 'success');
      loader.stopLoading();
      onComplete?.();
    } catch (err) {
      reportAppError(err as Error);
      showAlert('Failed to create units', 'error');
      loader.stopLoading();
    } finally {
      setSaving(false);
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'Unit', width: 80 },
    { field: 'sqft', headerName: 'Sqft', type: 'number', width: 80 },
    { field: 'numBedrooms', headerName: 'Beds', type: 'number', width: 60 },
    { field: 'numBathrooms', headerName: 'Baths', type: 'number', width: 60 },
    {
      field: 'marketRent',
      headerName: 'Market Rent',
      type: 'number',
      width: 110,
      renderCell: (params) => params.value ? `$${params.value.toLocaleString()}` : '--',
    },
    {
      field: 'tenantName',
      headerName: 'Tenant',
      width: 160,
      renderCell: (params) => params.value || '--',
    },
    {
      field: 'leaseStartDate',
      headerName: 'Lease From',
      width: 110,
      renderCell: (params) => params.value ? dayjs(params.value).format('MM/DD/YYYY') : '--',
    },
    {
      field: 'leaseEndDate',
      headerName: 'Lease To',
      width: 110,
      renderCell: (params) => params.value ? dayjs(params.value).format('MM/DD/YYYY') : '--',
    },
    {
      field: 'currentState',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => params.value === LeaseLifecycle.DONE ? 'Occupied' : 'Vacant',
    },
  ];

  return (
    <Stack gap="16px">
      <Typography variant="h6" color="textPrimary">
        Create Units from Entrata Export
      </Typography>

      <Alert severity="info">
        Upload the Unit Directory file first, then optionally add the Tenant Listing to include lease data.
      </Alert>

      <Stack direction="row" gap="10px" alignItems="center">
        <FileUploadButton
          accept=".xlsx,.xls"
          label="1. Unit Directory"
          onSelect={handleUnitDirectoryFile}
        />
        {unitFileName && (
          <Chip label={unitFileName} color="success" size="small" variant="outlined" />
        )}
      </Stack>

      <Stack direction="row" gap="10px" alignItems="center">
        <FileUploadButton
          accept=".xlsx,.xls"
          label="2. Tenant Listing (optional)"
          onSelect={handleTenantListingFile}
        />
        {tenantFileName && (
          <Chip label={tenantFileName} color="success" size="small" variant="outlined" />
        )}
      </Stack>

      {parsedUnits.length > 0 && (
        <>
          <Typography variant="body2" color="textSecondary">
            {parsedUnits.length} units found
            {tenantFileName && `, ${parsedUnits.filter((u) => u.tenantName).length} with tenant data`}
          </Typography>

          <DataGrid
            rows={parsedUnits}
            columns={columns}
            autoHeight
            density="compact"
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            pageSizeOptions={[25, 50, 100]}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateUnits}
            disabled={saving}
            fullWidth
          >
            {saving ? 'Creating Units...' : `Create ${parsedUnits.length} Units`}
          </Button>
        </>
      )}
    </Stack>
  );
}
